import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { type Condition, type ConditionType, type ValueKind, blankCondition, conditionTypesFor, isDateBound } from './definition'

export interface ValueOption {
  value: string
  label: string
}

interface ConditionBuilderProps {
  kind: ValueKind | null
  condition: Condition
  onChange: (next: Condition) => void
  /** Enum values of the source property, for an `in` condition. */
  options?: ValueOption[]
  /** Unit of a numeric source, shown next to the bounds. */
  unit?: string
  testId: string
}

const SELECT_CLASS = 'h-9 rounded-md border border-input bg-background px-2 text-sm'

function num(v: string): number | undefined {
  if (v.trim() === '') return undefined
  const n = Number(v.replace(',', '.'))
  return Number.isFinite(n) ? n : undefined
}

/**
 * The condition of one class. It offers only the condition types that fit the
 * source (a number gets a range, a text a text test, a date a date range...), so
 * an ill-typed condition cannot be built (spec 2026-10-06-11).
 */
export function ConditionBuilder({ kind, condition, onChange, options = [], unit, testId }: ConditionBuilderProps) {
  const { t } = useTranslation('thematics')
  const listId = useId()
  const types = conditionTypesFor(kind)
  const type = condition.type as ConditionType

  const setType = (next: ConditionType) => onChange(blankCondition(next))

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
      <select
        className={SELECT_CLASS}
        value={types.includes(type) ? type : ''}
        onChange={(e) => setType(e.target.value as ConditionType)}
        data-testid={`${testId}-type`}
        aria-label={t('definitions.condition.type')}
      >
        {!types.includes(type) && <option value="">{t('definitions.condition.pick')}</option>}
        {types.map((c) => (
          <option key={c} value={c} data-testid={`${testId}-type-${c}`}>{t(`definitions.condition.types.${c}`)}</option>
        ))}
      </select>

      {type === 'in' && (
        kind === 'enum' || kind === 'boolean'
          ? (
              <div className="flex flex-wrap gap-3" data-testid={`${testId}-values`}>
                {(kind === 'boolean'
                  ? [{ value: 'true', label: t('definitions.condition.yes') }, { value: 'false', label: t('definitions.condition.no') }]
                  : options
                ).map((o) => {
                  const checked = (condition.values ?? []).includes(o.value)
                  return (
                    <label key={o.value} className="flex items-center gap-1 text-sm">
                      <Checkbox
                        checked={checked}
                        data-testid={`${testId}-value-${o.value}`}
                        onCheckedChange={(on) => {
                          const cur = condition.values ?? []
                          onChange({ ...condition, values: on ? [...cur, o.value] : cur.filter((v) => v !== o.value) })
                        }}
                      />
                      {o.label}
                    </label>
                  )
                })}
              </div>
            )
          : (
              <>
                <Input
                  className="w-72"
                  list={listId}
                  value={(condition.values ?? []).join(', ')}
                  placeholder={t('definitions.condition.codes_placeholder')}
                  data-testid={`${testId}-values-input`}
                  onChange={(e) => onChange({ ...condition, values: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) })}
                />
                <datalist id={listId}>
                  {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </datalist>
              </>
            )
      )}

      {type === 'range' && (
        <>
          <Input
            className="w-36"
            inputMode="decimal"
            defaultValue={condition.min ?? ''}
            placeholder={t('definitions.condition.min')}
            data-testid={`${testId}-min`}
            aria-label={t('definitions.condition.min')}
            onChange={(e) => onChange({ ...condition, min: num(e.target.value) })}
          />
          <span className="text-muted-foreground">–</span>
          <Input
            className="w-36"
            inputMode="decimal"
            defaultValue={condition.max ?? ''}
            placeholder={t('definitions.condition.max')}
            data-testid={`${testId}-max`}
            aria-label={t('definitions.condition.max')}
            onChange={(e) => onChange({ ...condition, max: num(e.target.value) })}
          />
          {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
          <span className="text-xs text-muted-foreground">{t('definitions.condition.range_hint')}</span>
        </>
      )}

      {type === 'date_range' && <DateRange condition={condition} onChange={onChange} testId={testId} />}

      {type === 'text' && (
        <>
          <select
            className={SELECT_CLASS}
            value={condition.op ?? 'contains'}
            onChange={(e) => onChange({ ...condition, op: e.target.value as Condition['op'] })}
            data-testid={`${testId}-op`}
            aria-label={t('definitions.condition.op')}
          >
            {(['equals', 'contains', 'starts_with'] as const).map((op) => (
              <option key={op} value={op}>{t(`definitions.condition.ops.${op}`)}</option>
            ))}
          </select>
          <Input
            className="w-56"
            value={condition.value ?? ''}
            data-testid={`${testId}-text`}
            aria-label={t('definitions.condition.text')}
            onChange={(e) => onChange({ ...condition, value: e.target.value })}
          />
        </>
      )}
    </div>
  )
}

function DateRange({ condition, onChange, testId }: { condition: Condition, onChange: (c: Condition) => void, testId: string }) {
  const { t } = useTranslation('thematics')
  const years = condition.year_from !== undefined || condition.year_to !== undefined
  const setYears = (on: boolean) =>
    onChange(on ? { type: 'date_range', year_from: new Date().getFullYear() - 50 } : { type: 'date_range', from: 'P0D', to: 'P6M' })
  const bound = (key: 'from' | 'to', label: string) => {
    const value = condition[key] ?? ''
    return (
      <Input
        className={`w-36 ${isDateBound(value) ? '' : 'border-destructive'}`}
        value={value}
        placeholder={label}
        aria-label={label}
        aria-invalid={!isDateBound(value)}
        data-testid={`${testId}-${key}`}
        onChange={(e) => onChange({ ...condition, [key]: e.target.value || undefined })}
      />
    )
  }
  return (
    <>
      <select
        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        value={years ? 'years' : 'dates'}
        onChange={(e) => setYears(e.target.value === 'years')}
        data-testid={`${testId}-date-mode`}
        aria-label={t('definitions.condition.date_mode')}
      >
        <option value="dates">{t('definitions.condition.date_modes.dates')}</option>
        <option value="years">{t('definitions.condition.date_modes.years')}</option>
      </select>
      {years
        ? (
            <>
              <Input
                className="w-24"
                inputMode="numeric"
                defaultValue={condition.year_from ?? ''}
                placeholder={t('definitions.condition.year_from')}
                aria-label={t('definitions.condition.year_from')}
                data-testid={`${testId}-year-from`}
                onChange={(e) => onChange({ ...condition, year_from: num(e.target.value) })}
              />
              <span className="text-muted-foreground">–</span>
              <Input
                className="w-24"
                inputMode="numeric"
                defaultValue={condition.year_to ?? ''}
                placeholder={t('definitions.condition.year_to')}
                aria-label={t('definitions.condition.year_to')}
                data-testid={`${testId}-year-to`}
                onChange={(e) => onChange({ ...condition, year_to: num(e.target.value) })}
              />
            </>
          )
        : (
            <>
              {bound('from', t('definitions.condition.from'))}
              <span className="text-muted-foreground">→</span>
              {bound('to', t('definitions.condition.to'))}
              <span className="text-xs text-muted-foreground">{t('definitions.condition.date_hint')}</span>
            </>
          )}
    </>
  )
}
